import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./examMarkEntry.module.css";
import { FaUser, FaStar } from "react-icons/fa";
import AxiosInstance, {
  IMAGE_BASE_URL,
  TokenService,
} from "../../../services/Api";
import {
  CustomButton,
  CustomDropdown,
  CustomTextInput,
} from "../../../components";
import moment from "moment";
import PopupModal from "../../../components/PopupModal/PopupModal";

function VerifyMark() {
  const navigate = useNavigate();
  const location = useLocation();
  const { CLASSID, SECTION, SUBJECTID, MODEOFEXAM, EXAMNAME } =
    location.state || {};
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || "";
  const staff_id = clientData?.master_id;
  const userId = user?.id || 0;
  const academic_year_id = clientData?.academic_year_id || "";

  const [examData, setExamData] = useState([]);
  const [areAllChecked, setAreAllChecked] = useState(false);
  const [classDetails, setClassDetails] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  const handleMarkChange = (studentId, value) => {
    setExamData((prev) =>
      prev.map((student) =>
        student.student_id === studentId
          ? { ...student, marks: value }
          : student,
      ),
    );
  };

  useEffect(() => {
    fetchViewMarkEntry();
  }, [CLASSID, SECTION, SUBJECTID, MODEOFEXAM, EXAMNAME]);

  const fetchViewMarkEntry = async () => {
    setLoading(true);
    try {
      const payload = {
        school_id: parseInt(school_id),
        staff_id: staff_id,
        class_id: CLASSID,
        section_id: SECTION,
        subject_id: SUBJECTID,
        config_id: EXAMNAME,
        mode_exam: MODEOFEXAM,
      };
      const response = await AxiosInstance.post(
        `/exam/examMarkEntry/view`,
        payload,
      );
      if (response?.data?.status) {
        setClassDetails(response.data.data);
        const examData = response.data.data.students || [];
        setExamData(examData);
      } else {
        setExamData([]);
      }
    } catch (error) {
      console.error("Error fetching exam config list:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    return newErrors;
  };

  const handleDraft = async (e) => {
    e.preventDefault();

    const selectedStudents = examData.filter((student) => student.is_selected);

    // ✅ Show PopupModal instead of alert when not all students are selected
    if (selectedStudents.length !== examData.length || examData.length === 0) {
      setModalState({
        open: true,
        type: "error",
        message:
          translations?.labels?.selectAllStudentsToVerify ||
          "Please select all students to verify.",
        redirectPath: "",
      });
      return;
    }

    const validationErrors = validateForm();
    setErrors(validationErrors);

    const marksDetails = examData.map((student) => ({
      id: student.id,
      student_id: student.student_id,
      marks: Number(student.marks) || 0,
    }));

    const payload = {
      client_id: client_id,
      academic_id: academic_year_id,
      staff_id: staff_id,
      config_id: classDetails.examtype || "",
      school_id: school_id,
      class_id: classDetails.class_id || "",
      section_id: classDetails.section_id || "",
      subject_id: classDetails.subject_id || "",
      modeof_exam: classDetails.modeof_exam || "",
      campus_exam_schedule_id: classDetails.campus_exam_schedule_id,
      exam_date: classDetails.exam_date,
      marks_details: marksDetails,
      min_marks: classDetails.min_marks,
      max_marks: classDetails.max_marks,
      is_verify: 1,
      isActive: 1,
      updated_by: userId,
    };

    const response = await AxiosInstance.put(
      `/exam/examMarkEntry/update`,
      payload,
    );

    if (response.data?.status) {
      setModalState({
        open: true,
        type: "success",
        message: "Mark verified  successfully",
        redirectPath: "/user/exams/marks",
      });
    } else {
      setModalState({
        open: true,
        type: "error",
        message: response?.data?.message || "Something went wrong!",
        redirectPath: "",
      });
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleCheckboxChange = (studentId, checked) => {
    setExamData((prev) =>
      prev.map((user) =>
        user.student_id === studentId
          ? { ...user, is_selected: checked }
          : user,
      ),
    );
  };

  const handleCheckAllToggle = () => {
    const newValue = !areAllChecked;
    setAreAllChecked(newValue);
    setExamData((prev) =>
      prev.map((user) => ({
        ...user,
        is_selected: newValue,
      })),
    );
  };

  useEffect(() => {
    if (classDetails?.verify_marks !== undefined) {
      const shouldCheckAll = classDetails.verify_marks === 1;
      setExamData((prev) =>
        prev.map((user) => ({
          ...user,
          is_selected: shouldCheckAll,
        })),
      );
      setAreAllChecked(shouldCheckAll);
    }
  }, [classDetails?.verify_marks]);

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      <div className={styles.container}>
        <form className="row g-3 align-items-center">
          {/* Class */}
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels.class}
              name="classValue"
              value={classDetails.class_name}
              disabled
              placeholder={translations.labels.selectClass}
              isSearchable={false}
            />
          </div>

          {/* Section */}
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels.section}
              name="section"
              value={classDetails.section_id}
              placeholder={translations.labels.selectSection}
              disabled
              isSearchable={false}
            />
          </div>

          {/* Subject */}
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels.subject}
              name="subject"
              value={classDetails.subject_name}
              placeholder={translations.labels.selectsubjectPlaceholder}
              disabled
              isSearchable={false}
            />
          </div>

          {/* Exam Name */}
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels.examName || "Exam Name"}
              name="examName"
              value={classDetails.exam_name}
              placeholder={translations.labels.selectsubjectPlaceholder}
              disabled
              isSearchable={false}
            />
          </div>

          {/* Exam Mode */}
          <div className="col-lg-3 px-2 col-md-6 col-sm-12 mt-3">
            <CustomDropdown
              label={translations.labels.modeOfName || "Exam Mode"}
              name="modeOfName"
              value={classDetails.modeof_exam}
              placeholder={translations.labels.selectsubjectPlaceholder}
              disabled
              isSearchable={false}
            />
          </div>

          <>
            <div className="col-lg-3 col-md-6 col-sm-12 px-2">
              <CustomTextInput
                label={translations.labels.passMark || "Pass Mark"}
                value={classDetails.min_marks}
                disabled
              />
            </div>
            <div className="col-lg-2 col-md-6 col-sm-12 px-2">
              <CustomTextInput
                label={translations.labels.maxMark || "Max Mark"}
                value={classDetails.max_marks}
                disabled
              />
            </div>
            <div className="col-lg-2 col-md-6 col-sm-12 px-2">
              <CustomTextInput
                label={translations.labels.examDate || "Exam Date"}
                value={
                  classDetails?.exam_date
                    ? moment(classDetails.exam_date, "DD-MM-YYYY").format(
                        "DD/MM/YYYY",
                      )
                    : ""
                }
                disabled
              />
            </div>
            <div className="col-lg-2 col-md-6 col-sm-12 px-2">
              <button
                type="button"
                className={`${styles.checkAllBtn} ${
                  areAllChecked ? styles.uncheck : styles.check
                }`}
                onClick={handleCheckAllToggle}
              >
                {areAllChecked ? "✕ Uncheck All" : "✓ Check All"}
              </button>
            </div>
          </>

          <div className={styles.wrapper}>
            {examData.map((user, index) => (
              <div key={user.id} className={styles.card}>
                {/* Left Section */}
                <div className={styles.leftSection}>
                  <div className={styles.avatarWrapper}>
                    <div
                      className={`${styles.avatar} 
                        ${user.is_top === 1 ? styles.topBorder : ""} 
                        ${user.is_fail === 1 ? styles.failBorder : ""}
                      `}
                    >
                      {user.student_profile ? (
                        <img
                          src={`${IMAGE_BASE_URL}${user.student_profile}`}
                          alt={user.name}
                          className={styles.profileImage}
                        />
                      ) : (
                        <FaUser className={styles.defaultIcon} />
                      )}
                    </div>
                    {user.is_top === 1 && <FaStar className={styles.topStar} />}
                  </div>
                  <div>
                    <div className={styles.phone}>{user.student_id}</div>
                    <div className={styles.name}>{user.name}</div>
                  </div>
                </div>

                {/* Right Section */}
                <div className={styles.rightSection}>
                  <input
                    type="number"
                    className={styles.markInput}
                    value={user.marks ?? ""}
                    disabled
                    onChange={(e) =>
                      handleMarkChange(user.student_id, e.target.value)
                    }
                  />
                </div>
                <input
                  type="checkbox"
                  className={styles.checkBox}
                  checked={user.is_selected || false}
                  onChange={(e) =>
                    handleCheckboxChange(user.student_id, e.target.checked)
                  }
                />
                {index !== examData.length - 1 && (
                  <div className={styles.verticalDivider}></div>
                )}
              </div>
            ))}
          </div>

          <div className="col-md-12 pt-3">
            <div className={styles.buttonGroup}>
              <CustomButton
                variant="contained"
                onClick={handleDraft}
                className={styles.saveButton}
                loading={loading}
              >
                {translations.labels.submitButton || "Submit"}
              </CustomButton>
              <CustomButton
                variant="contained"
                onClick={handleCancel}
                className={styles.cancelButton}
                disabled={loading}
              >
                {translations.labels.cancelButton}
              </CustomButton>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default VerifyMark;
